const zendeskLoaded = () => {
  if (window.zE) return Promise.resolve()

  return window.App.loadZendeskMessenger?.() ?? Promise.reject(new Error("Zendesk messenger is not on this page"))
}

// A conversation started before the login ends belongs to an anonymous Zendesk user, and the
// messenger then shows "offline". The login callback tells when the user is signed in.
const zendeskSignedIn = () =>
  new Promise<void>((resolve, reject) => {
    window.zE!(
      "messenger",
      "loginUser",
      (callback: (jwt: string) => void) => callback(window.App.zendeskWidgetJWT ?? ""),
      (error: Error | null) => (error ? reject(error) : resolve()),
    )
  })

interface ConversationResponse {
  id?: string
}

interface SupportConversation {
  tag: string
  message: string
}

// The messenger keeps the tag, and any conversation metadata, for every conversation started on
// this page until a reset or a sign-out. So the request sends no metadata, and it clears the tag
// as soon as its own conversation exists.
const startTaggedConversation = ({ tag, message }: SupportConversation) =>
  new Promise<ConversationResponse | undefined>((resolve, reject) => {
    const zE = window.zE!
    zE("messenger:set", "conversationTags", [tag])
    zE(
      "messenger",
      "newConversation",
      { message: { content: { type: "text", text: message } } },
      (error: Error | null, response?: ConversationResponse) => {
        zE("messenger:set", "conversationTags", [])
        if (error) reject(error)
        else resolve(response)
      },
    )
  })

const showConversation = (response?: ConversationResponse) => {
  const zE = window.zE!
  const conversationId = response?.id
  zE("messenger", "open")
  zE(
    "messenger:ui",
    "navigation",
    conversationId
      ? { screen: "Conversation", options: { conversationId } }
      : { screen: "MostRecentActiveConversation" },
  )
}

// Opens a new support conversation that starts with the message. Support counts the
// conversations by the tag.
export default function openSupportConversation(conversation: SupportConversation) {
  return zendeskLoaded()
    .then(zendeskSignedIn)
    .then(() => startTaggedConversation(conversation))
    .then(showConversation)
    .catch(() => window.location.assign("/contact"))
}
